import { SearchOffOutlined as SearchOffIcon } from "@mui/icons-material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { CantFindOccurrence } from "@/hooks/useCantFindOccurrences";
import OccurrencesTableBase, {
  buildOccurrenceStoreLabel,
  defaultOccurrenceCsvTime,
  defaultOccurrenceTimeCell,
  type OccurrenceColumn,
} from "@/pages/reports/shared/OccurrencesTableBase";
import { Store } from "@/services/features/StoreService";
import { formatDurationShort } from "@/utils/time";

interface CantFindOccurrencesTableProps {
  data: CantFindOccurrence[];
  loading: boolean;
  error: string | null;
  storeTimezones: Record<number, string>;
  associateFilter?: string | null;
  allAssociates: string[];
  onAssociateChange: (name: string | null) => void;
  onRequestImage?: (occurrence: CantFindOccurrence) => void;
  stores?: Store[];
  loadingKey: string;
  displayedDayCount: number;
}

const CantFindOccurrencesTable: React.FC<CantFindOccurrencesTableProps> = ({
  data,
  loading,
  error,
  storeTimezones,
  associateFilter = null,
  allAssociates,
  onAssociateChange,
  onRequestImage,
  stores = [],
  loadingKey,
  displayedDayCount,
}) => {
  const { t, i18n } = useTranslation();

  const columns = React.useMemo<OccurrenceColumn<CantFindOccurrence>[]>(
    () => [
      {
        id: "time",
        mobileRole: "summary",
        label: t("storeInsights.cantFindOccurrencesTable.columns.time"),
        sortKey: "createdAt",
        headerSx: { minWidth: 145 },
        cellSx: { minWidth: 145 },
        renderCell: defaultOccurrenceTimeCell,
        getCsvValue: defaultOccurrenceCsvTime,
      },
      {
        id: "image",
        mobileRole: "thumbnail",
        label: t("storeInsights.cantFindOccurrencesTable.columns.image"),
        renderCell: (row, context) => context.renderImageCell(row),
      },
      {
        id: "product",
        mobileRole: "identity",
        label: t("storeInsights.cantFindOccurrencesTable.columns.product"),
        sortKey: "productName",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.productName,
        getCsvValue: (row) => row.productName,
      },
      {
        id: "value",
        label: t("storeInsights.cantFindOccurrencesTable.columns.value"),
        sortKey: "value",
        cellClassName: "text-[#001F3F]",
        renderCell: (row, context) =>
          row.value ? context.formatOccurrenceAmount(row.value, row.storeId) : "--",
        getCsvValue: (row, context) =>
          row.value ? context.formatOccurrenceAmountCsv(row.value, row.storeId) : "--",
      },
      {
        id: "sku",
        label: t("storeInsights.cantFindOccurrencesTable.columns.sku"),
        sortKey: "sku",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.sku,
        getCsvValue: (row) => row.sku,
      },
      {
        id: "size",
        label: t("storeInsights.cantFindOccurrencesTable.columns.size"),
        sortKey: "size",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.size,
        getCsvValue: (row) => row.size,
      },
      {
        id: "color",
        label: t("storeInsights.cantFindOccurrencesTable.columns.color"),
        sortKey: "color",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.color,
        getCsvValue: (row) => row.color,
      },
      {
        id: "store",
        label: t("storeInsights.cantFindOccurrencesTable.columns.store"),
        sortKey: "storeName",
        sortAccessor: (row) => `${row.storeName} ${row.storeNumber}`,
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => buildOccurrenceStoreLabel(row.storeName, row.storeNumber),
        getCsvValue: (row) => buildOccurrenceStoreLabel(row.storeName, row.storeNumber),
      },
      {
        id: "associate",
        label: t("storeInsights.cantFindOccurrencesTable.columns.associate"),
        sortKey: "associateName",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.associateName,
        getCsvValue: (row) => row.associateName,
      },
      {
        id: "searchTime",
        mobileRole: "summary",
        label: t("storeInsights.cantFindOccurrencesTable.columns.searchTime"),
        sortKey: "searchTimeSeconds",
        align: "right",
        cellClassName: "text-[#001F3F]",
        renderCell: (row, context) =>
          formatDurationShort(row.searchTimeSeconds, context.currentLanguage),
        getCsvValue: (row, context) =>
          formatDurationShort(row.searchTimeSeconds, context.currentLanguage),
      },
    ],
    [t],
  );

  return (
    <OccurrencesTableBase
      resetKey={loadingKey}
      title={t("storeInsights.cantFindOccurrencesTable.title")}
      data={data}
      loading={loading}
      error={error}
      storeTimezones={storeTimezones}
      columns={columns}
      emptyMessage={
        associateFilter
          ? t("storeInsights.cantFindOccurrencesTable.empty.noRequestsForAssociate", {
              associateName: associateFilter,
            })
          : t("storeInsights.cantFindOccurrencesTable.empty.noRequests")
      }
      emptyState={
        <AdminEmptyState
          icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
          text={
            associateFilter
              ? t("storeInsights.cantFindOccurrencesTable.empty.noRequestsForAssociate", {
                  associateName: associateFilter,
                })
              : t("storeInsights.cantFindOccurrencesTable.empty.noRequests")
          }
        />
      }
      loadingCallout={
        <LongRunningDataLoader
          variant="table"
          isLoading={true}
          baseLabels={[
            t("storeInsights.common.loading.longRunningReport"),
            t("storeInsights.common.loading.readingActivity"),
            t("storeInsights.common.loading.matchingData"),
            t("storeInsights.common.loading.preparingResults"),
          ]}
          loopingLabels={[
            t("storeInsights.common.loading.stillWorking"),
            t("storeInsights.common.loading.thanksForPatience"),
          ]}
          estimate={{
            meanTimePerStoreMs: 1_500,
            storeCount: stores.length,
            displayedDayCount,
            growth: "logarithmic",
            logarithmicLinearWeight: 0.1,
          }}
          locale={i18n.resolvedLanguage || i18n.language}
          loadingKey={loadingKey}
          estimateLabel={(duration) => t("storeComparison.loading.usuallyUnder", { duration })}
        />
      }
      renderLeadingHeaderActions={() => (
        <MuiAutocompleteMultiSelect
          size="small"
          sx={{ minWidth: 280 }}
          label={t("storeInsights.cantFindOccurrencesTable.fields.associate")}
          value={associateFilter ? [associateFilter] : [""]}
          options={["", ...allAssociates.slice().sort((a, b) => a.localeCompare(b))]}
          onChange={(next) => onAssociateChange(next[0] || null)}
          getOptionLabel={(option) =>
            option || t("storeInsights.cantFindOccurrencesTable.options.allAssociates")
          }
          getOptionKey={(option) => option || "__all__"}
        />
      )}
      onRequestImage={onRequestImage}
      stores={stores}
      noDecimalAmounts
      paginate
    />
  );
};

export default CantFindOccurrencesTable;
